import { apiClient } from './client'

/**
 * The `/v1/admin/*` endpoints, as a drop-in for the api repo's own
 * `ApiClient` class — same method names, so the admin screens folded in from
 * that repo work unchanged.
 *
 * What is different is underneath: these go through the one shared client,
 * so the admin UI gets the in-memory token, the single-flight refresh and
 * the same error type as the cockpit, instead of its own axios instance with
 * a token in `localStorage`.
 *
 * Access control is enforced by the gateway on every one of these calls. The
 * admin shell only decides which screen to render, and folding it into a
 * build served from a public host does not change who may call them.
 */
export const adminApi = {
  healthCheck: async () => (await apiClient.axios.get('/health')).data,

  getJobs: async (params?: {
    status?: string
    template_id?: string
    limit?: number
    offset?: number
    sort_by?: string
    sort_order?: string
  }) => (await apiClient.axios.get('/v1/admin/jobs', { params })).data,

  getJob: async (jobId: string) => (await apiClient.axios.get(`/v1/admin/jobs/${jobId}`)).data,

  retryJob: async (jobId: string) =>
    (await apiClient.axios.post(`/v1/admin/jobs/${jobId}/retry`)).data,

  cancelJob: async (jobId: string) =>
    (await apiClient.axios.post(`/v1/admin/jobs/${jobId}/cancel`)).data,

  getJobStats: async () => (await apiClient.axios.get('/v1/admin/jobs/stats')).data,

  searchJobs: async (query: string, limit?: number) =>
    (await apiClient.axios.get('/v1/admin/jobs/search', { params: { q: query, limit } })).data,

  getQueueStats: async () => (await apiClient.axios.get('/v1/admin/queue/stats')).data,

  getDetailedHealth: async () => (await apiClient.axios.get('/v1/admin/health/detailed')).data,

  getWorkerMetrics: async () => (await apiClient.axios.get('/v1/admin/metrics/workers')).data,

  getTemplates: async () => (await apiClient.axios.get('/v1/admin/templates')).data,

  // API keys are per user, not per template: these endpoints are open to any
  // authenticated caller and scoped to them unless they are an admin.
  listAPIKeys: async () => (await apiClient.axios.get('/v1/admin/api-keys')).data,

  getAPIKey: async (keyId: string) =>
    (await apiClient.axios.get(`/v1/admin/api-keys/${keyId}`)).data,

  createAPIKey: async (data: {
    name: string
    description?: string
    metadata?: Record<string, string>
  }) => (await apiClient.axios.post('/v1/admin/api-keys', data)).data,

  updateAPIKey: async (keyId: string, data: { name?: string; is_active?: boolean }) =>
    (await apiClient.axios.patch(`/v1/admin/api-keys/${keyId}`, data)).data,

  deleteAPIKey: async (keyId: string) =>
    (await apiClient.axios.delete(`/v1/admin/api-keys/${keyId}`)).data,

  getAPIKeyUsage: async (keyId: string) =>
    (await apiClient.axios.get(`/v1/admin/api-keys/${keyId}/usage`)).data,
}
